<!--
  Configuration page cp (outgoing commission) — 04-frontend.md §6.

  Layout follows features/feature-bm/bd/page/bd-config/bd-config.page.html for the complex entity
  (one flat table per nav item, no inline children) and
  features/feature-bm/bm/page/overview/config/config.page.html for the height-constrained root
  `.config-content-container` (CONFIG-016) with a @switch on the active nav item.

  Every <mvs-widget> sits behind an @if on the nav item / inner tab, so it is instantiated — and
  only then fires its QL query — when the user actually opens that view (CONFIG-022).

  `cp.ProvisionType` is a COMPLEX configuration entity (eleven same-module children) and is
  therefore rendered as a plain table whose row click leaves this page (CONFIG-003 / CONFIG-015).
  Its children — cp.ProvisionTypeContractEvent / -PriceReason and cp.ProvisionTypeMatrix /
  -MatrixAgent included — are NOT part of this template; they belong to the object page, and so
  does the per-provision-type check report (04 §6, child 11).

  All nav items of this page are flat, full-width tables; the sidebar layouts (selectable parent +
  filtered child) live in the ProvisionType object page, not here.
-->
@if (initialized && activeNavItem) {
    <div class="config-content-container surface-0">

        @switch (activeNavItem) {

            <!-- ============================================================
                 Provisionstypen (default) — COMPLEX entity, flat table only.
                 Row click opens the object page with the left navigation over the
                 eleven children: Gruppen / Provisionsbasen / Provisionsarten /
                 Provisionsereignisse / Relevanz / Basiswert-Zuordnung /
                 Haftungs-Zuordnung / Matrix-Zuordnung / Matrizen /
                 Buchungsereignisse / Konfigurationsprüfung.

                 Target component: CpProvisionTypeObjectComponent
                 (component/object-components/cp-provision-type-object/), resolved
                 through the objectComponent[] entry in cp.entity-provider.ts.
                 ============================================================ -->
            @case ('cp.ProvisionType') {
                <div class="cp-config-flat-container">
                    <div class="surface-100 border-round p-3 mb-3 flex align-items-start gap-2">
                        <i class="fa-solid fa-circle-info text-primary mt-1"></i>
                        <div class="flex flex-column gap-1 text-sm">
                            <span class="font-semibold">Provisionstyp öffnen</span>
                            <span class="text-color-secondary">
                                Eine Zeile öffnet den Provisionstyp mit allen zugehörigen
                                Einstellungen — Gruppen, Provisionsbasen, Provisionsarten,
                                Provisionsereignisse samt Preisgründen, Relevanz, Basiswert- und
                                Haftungs-Zuordnung, Matrix-Zuordnung, Matrizen samt Matrixzeilen,
                                Buchungsereignisse und den Prüfbericht.
                            </span>
                        </div>
                    </div>
                    @if (widgets?.provisionTypeWidget?.widgetData) {
                        <mvs-widget
                                [widgetData]="widgets.provisionTypeWidget.widgetData"
                                (onObjectSelect)="handleProvisionTypeSelect($event)">
                        </mvs-widget>
                    }
                </div>
            }

            <!-- ============================================================
                 Haftungsarten — global catalogue, no ProvisionType FK
                 ============================================================ -->
            @case ('cp.ProvisionLiabilityType') {
                <div class="cp-config-flat-container">
                    @if (widgets?.provisionLiabilityTypeWidget?.widgetData) {
                        <mvs-widget
                                [widgetData]="widgets.provisionLiabilityTypeWidget.widgetData"
                                (onObjectSelect)="handleObjectSelect($event, 'cp.ProvisionLiabilityType')">
                        </mvs-widget>
                    }
                </div>
            }

            <!-- ============================================================
                 Kontobuchungsarten (Provisions-/Rücklagenkonto) — lookup catalogue
                 ============================================================ -->
            @case ('cp.ProvisionAgentTransactionType') {
                <div class="cp-config-flat-container">
                    @if (widgets?.provisionAgentTransactionTypeWidget?.widgetData) {
                        <mvs-widget
                                [widgetData]="widgets.provisionAgentTransactionTypeWidget.widgetData"
                                (onObjectSelect)="handleObjectSelect($event, 'cp.ProvisionAgentTransactionType')">
                        </mvs-widget>
                    }
                </div>
            }

            <!-- ============================================================
                 Vertragsbuchungsarten — counterpart of the account booking
                 ============================================================ -->
            @case ('cp.ProvisionContractTransactionType') {
                <div class="cp-config-flat-container">
                    @if (widgets?.provisionContractTransactionTypeWidget?.widgetData) {
                        <mvs-widget
                                [widgetData]="widgets.provisionContractTransactionTypeWidget.widgetData"
                                (onObjectSelect)="handleObjectSelect($event, 'cp.ProvisionContractTransactionType')">
                        </mvs-widget>
                    }
                </div>
            }

            <!-- ============================================================
                 Abrechnungskonten — master records + period chain of the group assignment.
                 The row click on an agent opens the ProvisionAgent object component
                 (CpProvisionAgentObjectComponent, registered in cp.entity-provider.ts),
                 where the chain of ONE agent is maintained (04 §7) — CONFIG-015.
                 ============================================================ -->
            @case ('cp.ProvisionAgent') {
                <p-tabs [value]="agentTabIndex" (valueChange)="onAgentTabChange($event)">
                    <p-tablist>
                        <p-tab [value]="0">
                            <i class="fa-solid fa-user-tie mr-2"></i>
                            Abrechnungskonten
                        </p-tab>
                        <p-tab [value]="1">
                            <i class="fa-solid fa-clock-rotate-left mr-2"></i>
                            Gruppenzuordnungen (Perioden)
                        </p-tab>
                    </p-tablist>
                    <p-tabpanels>
                        <p-tabpanel [value]="0">
                            @if (agentTabIndex === 0 && widgets?.provisionAgentWidget?.widgetData) {
                                <mvs-widget
                                        [widgetData]="widgets.provisionAgentWidget.widgetData"
                                        (onObjectSelect)="handleObjectSelect($event, 'cp.ProvisionAgent')">
                                </mvs-widget>
                            }
                        </p-tabpanel>
                        <p-tabpanel [value]="1">
                            @if (agentTabIndex === 1 && widgets?.provisionAgentGroupWidget?.widgetData) {
                                <mvs-widget
                                        [widgetData]="widgets.provisionAgentGroupWidget.widgetData"
                                        (onObjectSelect)="handleObjectSelect($event, 'cp.ProvisionAgentGroup')">
                                </mvs-widget>
                            }
                        </p-tabpanel>
                    </p-tabpanels>
                </p-tabs>
            }

        }

    </div>
}
